import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ISelectOption } from '@feathr/components';
import { Button, EmailInput, Input, Select } from '@feathr/components';

import * as styles from './UserInviteRow.css';

export interface IUserInviteData {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
}

interface IUserInviteRowProps {
  data: IUserInviteData;
  rowKey: string;
  fieldIdSuffix: string;
  showRemove: boolean;
  columnLabelIds: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  firstNameHadFocus: boolean;
  firstNameValidationErrors: string[];
  lastNameHadFocus: boolean;
  lastNameValidationErrors: string[];
  emailHadFocus: boolean;
  emailValidationErrors: string[];
  roleOptions: ISelectOption[];
  onUpdate: (id: string, data: IUserInviteData) => void;
  onRemove: (id: string) => void;
  onFirstNameBlur: (id: string) => void;
  onLastNameBlur: (id: string) => void;
  onEmailBlur: (id: string) => void;
}

function UserInviteRow({
  data,
  rowKey,
  fieldIdSuffix,
  showRemove,
  columnLabelIds,
  firstNameHadFocus,
  firstNameValidationErrors,
  lastNameHadFocus,
  lastNameValidationErrors,
  emailHadFocus,
  emailValidationErrors,
  roleOptions,
  onUpdate,
  onRemove,
  onFirstNameBlur,
  onLastNameBlur,
  onEmailBlur,
}: Readonly<IUserInviteRowProps>): JSX.Element {
  const { t } = useTranslation();

  const id = data.id ?? rowKey;
  const nameSuffix = fieldIdSuffix;

  function handleFirstNameChange(value?: string): void {
    onUpdate(id, { ...data, firstName: value ?? '' });
  }

  function handleLastNameChange(value?: string): void {
    onUpdate(id, { ...data, lastName: value ?? '' });
  }

  function handleEmailChange(value?: string): void {
    onUpdate(id, { ...data, email: value ?? '' });
  }

  function handleRoleChange(selectedRole: ISelectOption): void {
    onUpdate(id, { ...data, role: selectedRole.id });
  }

  function handleRemove(): void {
    onRemove(id);
  }

  function handleFirstNameBlur(): void {
    onFirstNameBlur(id);
  }

  function handleLastNameBlur(): void {
    onLastNameBlur(id);
  }

  function handleEmailBlur(): void {
    onEmailBlur(id);
  }

  function getRoleValue(): ISelectOption | undefined {
    return roleOptions.find(({ id }: ISelectOption): boolean => id === data.role);
  }

  return (
    <div className={styles.row}>
      <Input
        aria-labelledby={columnLabelIds.firstName}
        className={styles.firstName}
        maxLength={40}
        name={`first_name_${nameSuffix}`}
        onBlur={handleFirstNameBlur}
        onChange={handleFirstNameChange}
        required={true}
        showCounter={data.firstName.length > 0}
        type={'text'}
        validationError={firstNameHadFocus ? firstNameValidationErrors : []}
        value={data.firstName}
      />

      <Input
        aria-labelledby={columnLabelIds.lastName}
        className={styles.lastName}
        maxLength={40}
        name={`last_name_${nameSuffix}`}
        onBlur={handleLastNameBlur}
        onChange={handleLastNameChange}
        required={true}
        showCounter={data.lastName.length > 0}
        type={'text'}
        validationError={lastNameHadFocus ? lastNameValidationErrors : []}
        value={data.lastName}
      />

      <EmailInput
        allowMultiple={false}
        aria-labelledby={columnLabelIds.email}
        className={styles.emailField}
        name={`email_address_${nameSuffix}`}
        onBlur={handleEmailBlur}
        onChange={handleEmailChange}
        t={t}
        type={'email'}
        validationError={emailHadFocus ? emailValidationErrors : []}
        value={data.email}
      />

      <div className={styles.roleField}>
        <Select
          aria-labelledby={columnLabelIds.role}
          id={`role-select-${nameSuffix}`}
          name={`role-select-${nameSuffix}`}
          onSelectSingle={handleRoleChange}
          options={roleOptions}
          placeholder={t('Select a role...')}
          value={getRoleValue()}
        />
      </div>

      <div className={styles.removeButton}>
        {showRemove && (
          <Button
            aria-label={t('Remove user invitation')}
            className={styles.removeButtonIcon}
            onClick={handleRemove}
            type={'naked'}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </div>
    </div>
  );
}

export default UserInviteRow;
